// src/components/PackagesPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
} from "firebase/firestore";

const PackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  // ✅ Track logged-in user state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, [auth]);

  // ✅ Fetch packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const snapshot = await getDocs(collection(db, "packages"));
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setPackages(data);
      } catch (err) {
        console.error("Error fetching packages:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [db]);

  const handleStake = async (pkg) => {
    // Step 1: Check login
    if (!currentUser) {
      navigate("/signin");
      return;
    }

    const userRef = doc(db, "users", currentUser.uid);
    const activePackageCol = collection(userRef, "activePackage");

    try {
      // Step 2: Check if user already has active package
      const activeSnapshot = await getDocs(activePackageCol);
      if (!activeSnapshot.empty) {
        alert("⚠️ You already have an active package.");
        return;
      }

      // Step 3: Fetch user asset
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        alert("⚠️ User data not found!");
        return;
      }

      const asset = userSnap.data()?.asset || 0;
      if (asset < pkg.priceRange) {
        alert("⚠️ Insufficient balance.");
        return;
      }

      // Step 4: Deduct asset
      await updateDoc(userRef, { asset: asset - pkg.priceRange });

      // Step 5: Save active package in subcollection
      const purchasedPackage = {
        title: pkg.title,
        price: pkg.priceRange,
        incomePercent: pkg.income,
        fee: pkg.fee,
        durationDays: pkg.duration,
        level: pkg.level,
        purchasedAt: new Date(),
        claimedDays: {},
      };

      await addDoc(activePackageCol, purchasedPackage);

      alert(`🎉 Successfully purchased ${pkg.title}`);
    } catch (err) {
      console.error("❌ Error purchasing package:", err);
      alert("Something went wrong while purchasing.");
    }
  };

  if (loading) return <p className="text-center py-10">Loading packages...</p>;

  return (
    <div className="container mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold text-center mb-10 text-gray-800">
        💎 Available Packages
      </h1>

      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="bg-white shadow-xl rounded-2xl overflow-hidden hover:scale-[1.02] hover:shadow-2xl transition-transform flex flex-col"
          >
            <img
              src={pkg.imageUrl}
              alt={pkg.title}
              className="w-full h-48 object-cover"
            />

            <div className="p-6 flex flex-col flex-1">
              {/* Title + Status */}
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-gray-800">{pkg.title}</h2>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    pkg.status === "active"
                      ? "bg-green-100 text-green-600"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {pkg.status}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-1 text-gray-600 text-sm flex-1">
                <p>
                  💰 Price: <span className="font-medium">${pkg.priceRange}</span>
                </p>
                <p>
                  📈 Income: <span className="font-medium">{pkg.income}%</span>
                </p>
                <p>
                  ⏳ Duration: <span className="font-medium">{pkg.duration} days</span>
                </p>
                <p>
                  ⚡ Fee: <span className="font-medium">{pkg.fee}%</span>
                </p>
                <p>
                  ⭐ Level: <span className="font-medium">{pkg.level}</span>
                </p>
              </div>

              {/* Action */}
              <button
                onClick={() => handleStake(pkg)}
                className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-xl hover:bg-blue-700 transition font-medium"
              >
                Go to Stake
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PackagesPage;
